// src/pages/Privacy.tsx
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white flex flex-col">
      {/* ─────────── HEADER ─────────── */}
      <header className="border-b border-white/10 bg-[#0b0b0f]/70 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">
            Ovylon <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
          </Link>

          <div className="flex gap-3">
            <Link
              to="/register"
              className="px-4 py-2 bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition"
            >
              Crear cuenta
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 border border-white/15 rounded-lg hover:bg-white/10 transition"
            >
              Iniciar sesión
            </Link>
          </div>
        </div>
      </header>

      {/* ─────────── CONTENT ─────────── */}
      <main className="flex-1 max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Privacidad</div>
        <h1 className="text-3xl md:text-4xl font-bold mt-3">Política de Privacidad</h1>

        <p className="text-gray-400 mt-5 text-base md:text-lg max-w-4xl leading-relaxed">
          Esta Política explica cómo Ovylon (“Ovylon”, “nosotros”) recopila, usa y comparte información personal cuando
          visitas nuestro sitio o usas nuestra plataforma y herramientas de creación con IA (el “Servicio”).
        </p>

        <div className="mt-3 text-sm text-gray-500">
          <span className="text-gray-400">Vigente desde:</span>{" "}
          {new Date().toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric" })}
        </div>

        {/* Sections */}
        <div className="mt-10 space-y-8 max-w-5xl">
          {/* 1 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">1) Información que recopilamos</h2>

            <div className="mt-4 space-y-4 text-sm text-gray-300 leading-relaxed">
              <div>
                <div className="font-semibold text-gray-100">a) Información que nos das</div>
                <ul className="mt-2 space-y-2 list-disc list-inside text-gray-300">
                  <li>
                    <span className="text-gray-100 font-medium">Cuenta y contacto:</span> email, nombre (si lo
                    proporcionas) y credenciales de acceso.
                  </li>
                  <li>
                    <span className="text-gray-100 font-medium">Soporte:</span> lo que nos envías por formularios o email.
                  </li>
                  <li>
                    <span className="text-gray-100 font-medium">Contenido:</span> prompts, descripciones y archivos que subas
                    para generar o editar (por ejemplo imágenes), junto con metadatos técnicos asociados.
                  </li>
                </ul>
              </div>

              <div>
                <div className="font-semibold text-gray-100">b) Información que se recopila automáticamente</div>
                <ul className="mt-2 space-y-2 list-disc list-inside text-gray-300">
                  <li>
                    <span className="text-gray-100 font-medium">Datos del dispositivo y uso:</span> navegador, sistema
                    operativo, páginas visitadas, tiempos de carga, eventos de interacción y dirección IP (aproximada).
                  </li>
                  <li>
                    <span className="text-gray-100 font-medium">Cookies y tecnologías similares:</span> para recordar
                    preferencias, seguridad y analítica.
                  </li>
                </ul>
              </div>

              <div>
                <div className="font-semibold text-gray-100">c) Pagos</div>
                <p className="mt-2 text-gray-300">
                  Si compras un plan, el procesamiento de pagos lo realizan proveedores de pago. No almacenamos el número
                  completo de tu tarjeta; recibimos confirmaciones y datos necesarios para facturación y control de fraude.
                </p>
              </div>
            </div>
          </section>

          {/* 2 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">2) Cómo usamos tu información</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Usamos la información para operar, mantener y mejorar el Servicio, por ejemplo para:
            </p>

            <ul className="mt-4 space-y-2 list-disc list-inside text-sm text-gray-300">
              <li>Crear y administrar tu cuenta, autenticar acceso y mantener el Servicio funcionando.</li>
              <li>Procesar solicitudes: generación/edición de contenido, historial y gestión de créditos/planes.</li>
              <li>Soporte: responder preguntas, resolver problemas y enviar avisos operativos.</li>
              <li>Seguridad: prevenir abuso, fraude, accesos no autorizados y proteger la plataforma.</li>
              <li>Mejora del producto: analítica agregada para entender uso y optimizar rendimiento/UX.</li>
            </ul>

            <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-sm font-semibold text-gray-100">Sobre contenido (prompts e imágenes)</div>
              <p className="mt-2 text-sm text-gray-300 leading-relaxed">
                El contenido que generas puede almacenarse para darte funcionalidades como historial, reintentos, y una mejor
                experiencia de uso. Si más adelante activas opciones de borrado/exportación, esta política se actualizará para
                reflejarlo claramente.
              </p>
            </div>
          </section>

          {/* 3 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">3) Cómo compartimos tu información</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              No vendemos tus datos personales. Podemos compartir información con:
            </p>

            <ul className="mt-4 space-y-2 list-disc list-inside text-sm text-gray-300">
              <li>
                <span className="text-gray-100 font-medium">Proveedores</span> que nos ayudan a operar (hosting, base de datos,
                analítica, email, soporte y pagos).
              </li>
              <li>
                <span className="text-gray-100 font-medium">Cumplimiento legal</span> si una autoridad lo requiere
                válidamente o para proteger derechos, seguridad y prevenir fraude/abuso.
              </li>
              <li>
                <span className="text-gray-100 font-medium">Transferencias de negocio</span> (por ejemplo, fusión o adquisición),
                aplicando salvaguardas razonables.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">4) Retención</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Conservamos la información el tiempo necesario para operar el Servicio, cumplir obligaciones legales, resolver
              disputas y hacer cumplir acuerdos. El periodo exacto puede variar según el tipo de dato y el propósito.
            </p>
          </section>

          {/* 5 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">5) Tus opciones y derechos</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Dependiendo de tu ubicación, puedes tener derechos sobre tu información (acceso, corrección, eliminación,
              portabilidad u oposición a ciertos tratamientos).
            </p>

            <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-sm font-semibold text-gray-100">Cómo ejercerlos</div>
              <p className="mt-2 text-sm text-gray-300 leading-relaxed">
                Escríbenos a <span className="text-gray-100 font-semibold">soporte@ovylon.ai</span> indicando el motivo
                (por ejemplo: “Acceso”, “Borrado”, “Corrección”). Podremos pedirte verificación para proteger tu cuenta.
              </p>
            </div>
          </section>

          {/* 6 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">6) Seguridad</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Implementamos medidas técnicas y organizativas razonables para proteger la información. Ningún sistema es 100%
              infalible, pero trabajamos para reducir riesgos y responder ante incidentes.
            </p>
          </section>

          {/* 7 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">7) Transferencias internacionales</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Podemos procesar datos en países distintos al tuyo (por ejemplo, Estados Unidos) usando proveedores de
              infraestructura. Cuando aplique, adoptamos salvaguardas razonables para estas transferencias.
            </p>
          </section>

          {/* 8 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">8) Privacidad de menores</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              El Servicio no está dirigido a menores de 13 años. Si crees que un menor nos proporcionó datos, contáctanos para
              revisarlo.
            </p>
          </section>

          {/* 9 */}
          <section className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-lg font-semibold">9) Cambios a esta Política</h2>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Podemos actualizar esta Política para reflejar mejoras del Servicio o cambios legales. Publicaremos la versión
              actualizada en esta página y actualizaremos la fecha de vigencia.
            </p>
          </section>

          {/* Contact */}
          <section className="rounded-2xl border border-indigo-400/20 bg-gradient-to-br from-indigo-500/15 to-purple-500/10 p-7">
            <h2 className="text-lg font-semibold">Contacto</h2>
            <p className="mt-2 text-sm text-gray-300 leading-relaxed">
              Si tienes dudas sobre privacidad, escríbenos a{" "}
              <span className="text-gray-100 font-semibold">soporte@ovylon.ai</span>.
            </p>
          </section>
        </div>

        {/* ✅ Volver al inicio (uniforme, justo encima del footer) */}
        <div className="mt-12">
          <Link to="/" className="text-sm text-gray-300 hover:text-white transition">
            ← Volver al inicio
          </Link>
        </div>
      </main>

      {/* ─────────── FOOTER ─────────── */}
      <footer className="border-t border-white/10 py-10 text-sm text-gray-500">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>© {new Date().getFullYear()} Ovylon™ — All rights reserved.</div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/about" className="hover:text-gray-300 transition">
              Qué es Ovylon
            </Link>
            <Link to="/support" className="hover:text-gray-300 transition">
              Soporte
            </Link>
            <Link to="/privacy" className="hover:text-gray-300 transition">
              Privacidad
            </Link>
            <Link to="/terms" className="hover:text-gray-300 transition">
              Términos
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
